import { c as _c } from "react/compiler-runtime";
// Typography primitives for OG cards. Satori-compatible — uses inline
// styles only.
//
// IMPORTANT: never include undefined values in the style object. Satori
// parses the style map and calls `.trim()` on certain string values; an
// `opacity: undefined` entry crashes the renderer with
// "Cannot read properties of undefined (reading 'trim')". Spread the
// optional override only when defined.

import type { CSSProperties, ReactNode } from 'react';
import { OG_TOKENS } from '../tokens';
interface TextProps {
  children: ReactNode;
  /** Override the default color (e.g. for win/loss tinting). */
  color?: string;
  /** Override the default opacity. Omit for fully opaque. */
  opacity?: number;
  /** Extra inline styles merged onto the element. Optional. */
  style?: CSSProperties;
}
function mergeStyle(base: CSSProperties, override: CSSProperties | undefined): CSSProperties {
  if (override === undefined) return base;
  return {
    ...base,
    ...override
  };
}

/** 64–96px hero text. The marquee number on PnL / amount cards. */
export function OgKpi(props) {
  const $ = _c(7);
  const t0 = props.color ?? OG_TOKENS.gray50;
  let t1;
  if ($[0] !== props.opacity || $[1] !== props.style || $[2] !== t0) {
    const base = {
      display: "flex",
      fontSize: 96,
      fontWeight: 700,
      letterSpacing: -1.5,
      color: t0,
      ...(props.opacity !== undefined ? {
        opacity: props.opacity
      } : {})
    };
    t1 = mergeStyle(base, props.style);
    $[0] = props.opacity;
    $[1] = props.style;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.children || $[5] !== t1) {
    t2 = <span style={t1}>{props.children}</span>;
    $[4] = props.children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** 104px editorial headline. Static pages and other mono-shell taglines. */
export function OgEditorialTitle(props) {
  const $ = _c(7);
  const t0 = props.color ?? OG_TOKENS.gray50;
  let t1;
  if ($[0] !== props.opacity || $[1] !== props.style || $[2] !== t0) {
    const base = {
      display: "flex",
      fontSize: 104,
      fontWeight: 700,
      letterSpacing: -2,
      lineHeight: 1,
      color: t0,
      ...(props.opacity !== undefined ? {
        opacity: props.opacity
      } : {})
    };
    t1 = mergeStyle(base, props.style);
    $[0] = props.opacity;
    $[1] = props.style;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.children || $[5] !== t1) {
    t2 = <span style={t1}>{props.children}</span>;
    $[4] = props.children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** 40–56px primary heading. Title of a market, name of a gift recipient. */
export function OgHeading(props) {
  const $ = _c(7);
  const t0 = props.color ?? OG_TOKENS.gray50;
  let t1;
  if ($[0] !== props.opacity || $[1] !== props.style || $[2] !== t0) {
    const base = {
      display: "flex",
      fontSize: 48,
      fontWeight: 700,
      letterSpacing: -0.5,
      lineHeight: 1.15,
      color: t0,
      ...(props.opacity !== undefined ? {
        opacity: props.opacity
      } : {})
    };
    t1 = mergeStyle(base, props.style);
    $[0] = props.opacity;
    $[1] = props.style;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.children || $[5] !== t1) {
    t2 = <span style={t1}>{props.children}</span>;
    $[4] = props.children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** 24–32px supporting copy. Subtitles, descriptive sentences. */
export function OgBody(props) {
  const $ = _c(7);
  const t0 = props.color ?? OG_TOKENS.gray50;
  const t1 = props.opacity ?? 0.85;
  let t2;
  if ($[0] !== props.style || $[1] !== t0 || $[2] !== t1) {
    const base = {
      display: "flex",
      fontSize: 28,
      fontWeight: 400,
      lineHeight: 1.4,
      color: t0,
      opacity: t1
    };
    t2 = mergeStyle(base, props.style);
    $[0] = props.style;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.children || $[5] !== t2) {
    t3 = <span style={t2}>{props.children}</span>;
    $[4] = props.children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** 18–22px eyebrow. Surface-kind labels — "Gift", "Market". */
export function OgEyebrow(props) {
  const $ = _c(7);
  const t0 = props.color ?? OG_TOKENS.brand200;
  const t1 = props.opacity ?? 0.9;
  let t2;
  if ($[0] !== props.style || $[1] !== t0 || $[2] !== t1) {
    const base = {
      display: "flex",
      fontSize: 20,
      fontWeight: 600,
      color: t0,
      opacity: t1
    };
    t2 = mergeStyle(base, props.style);
    $[0] = props.style;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.children || $[5] !== t2) {
    t3 = <span style={t2}>{props.children}</span>;
    $[4] = props.children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}